import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Save, Plus, Trash2 } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";
import { EDITABLE_LANDING_PAGES } from "@/lib/landingPages";

interface FaqEntry {
  question: string;
  answer: string;
}

interface GuideEntry {
  emoji: string;
  title: string;
  body: string;
  image: string;
}

interface MatchEntry {
  area: string;
  price: string;
  description: string;
  featured: boolean;
}

interface SectionEntry {
  title: string;
  body: string;
}

interface Content {
  sections: SectionEntry[];
  hero_title: string;
  hero_subtitle: string;
  hero_primary_label: string;
  hero_secondary_label: string;
  section1_title: string;
  section1_body: string;
  listings_title: string;
  listings_note: string;
  match_title: string;
  match_intro: string;
  match_entries: MatchEntry[];
  section3_title: string;
  section3_body: string;
  section4_title: string;
  section4_intro: string;
  section4_features: string[];
  section5_title: string;
  section5_body: string;
  section5_button: string;
  section6_title: string;
  section6_body: string;
  section6_bullets: string[];
  guide_title: string;
  guide_intro: string;
  guide_entries: GuideEntry[];
  faq: FaqEntry[];
  closing_title: string;
  closing_body: string;
  closing_button: string;
}

const empty: Content = {
  sections: [],
  hero_title: "", hero_subtitle: "", hero_primary_label: "", hero_secondary_label: "",
  section1_title: "", section1_body: "",
  listings_title: "", listings_note: "",
  match_title: "", match_intro: "", match_entries: [],
  section3_title: "", section3_body: "",
  section4_title: "", section4_intro: "", section4_features: [],
  section5_title: "", section5_body: "", section5_button: "",
  section6_title: "", section6_body: "", section6_bullets: [],
  guide_title: "", guide_intro: "", guide_entries: [],
  faq: [],
  closing_title: "", closing_body: "", closing_button: "",
};

const LandingContentEditor = () => {
  const { toast } = useToast();
  const [slug, setSlug] = useState(EDITABLE_LANDING_PAGES[0].slug);
  const [c, setC] = useState<Content>(empty);
  const [loading, setLoading] = useState(false);

  const page = EDITABLE_LANDING_PAGES.find((p) => p.slug === slug)!;
  const isHub = page.kind === "hub";

  useEffect(() => {
    let active = true;
    (async () => {
      setC(empty);
      const { data } = await (supabase as any)
        .from("landing_pages").select("content").eq("slug", slug).maybeSingle();
      if (active && data?.content) setC({ ...empty, ...data.content });
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const save = async () => {
    setLoading(true);
    const { error } = await (supabase as any)
      .from("landing_pages")
      .upsert({ slug, content: c }, { onConflict: "slug" });
    setLoading(false);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "Conteúdo da landing salvo" });
  };

  const set = <K extends keyof Content>(k: K, v: Content[K]) => setC((p) => ({ ...p, [k]: v }));

  const editSection = (idx: number, patch: Partial<SectionEntry>) => {
    const arr = [...c.sections]; arr[idx] = { ...arr[idx], ...patch }; set("sections", arr);
  };
  const addSection = () => set("sections", [...c.sections, { title: "", body: "" }]);
  const removeSection = (idx: number) => set("sections", c.sections.filter((_, i) => i !== idx));

  const editArray = (key: "section4_features" | "section6_bullets", idx: number, value: string) => {
    const arr = [...c[key]]; arr[idx] = value; set(key, arr);
  };
  const addArray = (key: "section4_features" | "section6_bullets") => set(key, [...c[key], ""]);
  const removeArray = (key: "section4_features" | "section6_bullets", idx: number) =>
    set(key, c[key].filter((_, i) => i !== idx));

  const editFaq = (idx: number, patch: Partial<FaqEntry>) => {
    const arr = [...c.faq]; arr[idx] = { ...arr[idx], ...patch }; set("faq", arr);
  };
  const addFaq = () => set("faq", [...c.faq, { question: "", answer: "" }]);
  const removeFaq = (idx: number) => set("faq", c.faq.filter((_, i) => i !== idx));

  const editGuide = (idx: number, patch: Partial<GuideEntry>) => {
    const arr = [...c.guide_entries]; arr[idx] = { ...arr[idx], ...patch }; set("guide_entries", arr);
  };
  const addGuide = () =>
    set("guide_entries", [...c.guide_entries, { emoji: "", title: "", body: "", image: "" }]);
  const removeGuide = (idx: number) =>
    set("guide_entries", c.guide_entries.filter((_, i) => i !== idx));

  const editMatch = (idx: number, patch: Partial<MatchEntry>) => {
    const arr = [...c.match_entries]; arr[idx] = { ...arr[idx], ...patch }; set("match_entries", arr);
  };
  const addMatch = () =>
    set("match_entries", [...c.match_entries, { area: "", price: "", description: "", featured: false }]);
  const removeMatch = (idx: number) =>
    set("match_entries", c.match_entries.filter((_, i) => i !== idx));

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold">Conteúdo das Landing Pages</h2>
        <Button onClick={save} disabled={loading}><Save className="h-4 w-4 mr-1" /> Salvar</Button>
      </div>

      <div className="space-y-1">
        <Label>Landing page a editar</Label>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          {EDITABLE_LANDING_PAGES.map((p) => (
            <option key={p.slug} value={p.slug}>{p.label}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          {page.path} — {isHub ? "estrutura completa" : "hero, seções de texto e FAQ"}
        </p>
      </div>

      {/* SEÇÕES DE TEXTO (todas as landings) */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seções de texto</legend>
        {c.sections.map((sec, i) => (
          <div key={i} className="border border-border rounded p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Seção {i + 1}</span>
              <Button type="button" size="icon" variant="ghost" onClick={() => removeSection(i)}><Trash2 className="h-3 w-3" /></Button>
            </div>
            <Input placeholder="Título" value={sec.title} onChange={(e) => editSection(i, { title: e.target.value })} />
            <Textarea rows={5} placeholder="Texto" value={sec.body} onChange={(e) => editSection(i, { body: e.target.value })} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addSection}><Plus className="h-3 w-3 mr-1" /> Adicionar seção</Button>
      </fieldset>

      {/* HERO */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Hero</legend>
        <div><Label>H1 (Título principal)</Label><Input value={c.hero_title} onChange={(e) => set("hero_title", e.target.value)} /></div>
        <div><Label>Subtítulo</Label><Textarea rows={4} value={c.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Botão primário</Label><Input value={c.hero_primary_label} onChange={(e) => set("hero_primary_label", e.target.value)} /></div>
          <div><Label>Botão WhatsApp</Label><Input value={c.hero_secondary_label} onChange={(e) => set("hero_secondary_label", e.target.value)} /></div>
        </div>
      </fieldset>

      {isHub && (
        <>
      {/* SECTION 1 */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 1 — Introdução</legend>
        <div><Label>Título</Label><Input value={c.section1_title} onChange={(e) => set("section1_title", e.target.value)} /></div>
        <div><Label>Texto</Label><Textarea rows={6} value={c.section1_body} onChange={(e) => set("section1_body", e.target.value)} /></div>
      </fieldset>

      {/* LISTINGS */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 2 — Apartamentos disponíveis</legend>
        <div><Label>Título da seção</Label><Input value={c.listings_title} onChange={(e) => set("listings_title", e.target.value)} /></div>
        <div><Label>Nota explicativa</Label><Textarea rows={2} value={c.listings_note} onChange={(e) => set("listings_note", e.target.value)} /></div>
      </fieldset>

      {/* MATCH — Qual apartamento combina com você */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
          Qual apartamento combina com você
        </legend>
        <div><Label>Título da seção</Label><Input value={c.match_title} onChange={(e) => set("match_title", e.target.value)} placeholder="Qual apartamento combina com você?" /></div>
        <div><Label>Texto de introdução</Label><Textarea rows={2} value={c.match_intro} onChange={(e) => set("match_intro", e.target.value)} /></div>
        <Label className="mt-2">Perfis</Label>
        {c.match_entries.map((m, i) => (
          <div key={i} className="border border-border rounded p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Perfil {i + 1}</span>
              <Button type="button" size="icon" variant="ghost" onClick={() => removeMatch(i)}><Trash2 className="h-3 w-3" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Área (ex.: 100m²)</Label><Input value={m.area} onChange={(e) => editMatch(i, { area: e.target.value })} /></div>
              <div><Label className="text-xs">Preço (ex.: R$ 2.499.000)</Label><Input value={m.price} onChange={(e) => editMatch(i, { price: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Descrição do perfil</Label><Textarea rows={3} value={m.description} onChange={(e) => editMatch(i, { description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={m.featured} onChange={(e) => editMatch(i, { featured: e.target.checked })} />
              Marcar como destaque (⭐)
            </label>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addMatch}><Plus className="h-3 w-3 mr-1" /> Adicionar perfil</Button>
      </fieldset>

      {/* SECTION 3 */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 3 — Por que morar no bairro</legend>
        <div><Label>Título</Label><Input value={c.section3_title} onChange={(e) => set("section3_title", e.target.value)} /></div>
        <div><Label>Texto</Label><Textarea rows={6} value={c.section3_body} onChange={(e) => set("section3_body", e.target.value)} /></div>
      </fieldset>

      {/* SECTION 4 */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 4 — Diferenciais</legend>
        <div><Label>Título</Label><Input value={c.section4_title} onChange={(e) => set("section4_title", e.target.value)} /></div>
        <div><Label>Introdução</Label><Textarea rows={2} value={c.section4_intro} onChange={(e) => set("section4_intro", e.target.value)} /></div>
        <Label>Lista de diferenciais</Label>
        {c.section4_features.map((f, i) => (
          <div key={i} className="flex gap-2">
            <Input value={f} onChange={(e) => editArray("section4_features", i, e.target.value)} />
            <Button type="button" size="icon" variant="ghost" onClick={() => removeArray("section4_features", i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addArray("section4_features")}><Plus className="h-3 w-3 mr-1" /> Adicionar diferencial</Button>
      </fieldset>

      {/* SECTION 5 */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 5 — Lead magnet</legend>
        <div><Label>Título</Label><Input value={c.section5_title} onChange={(e) => set("section5_title", e.target.value)} /></div>
        <div><Label>Texto</Label><Textarea rows={4} value={c.section5_body} onChange={(e) => set("section5_body", e.target.value)} /></div>
        <div><Label>Texto do botão</Label><Input value={c.section5_button} onChange={(e) => set("section5_button", e.target.value)} /></div>
      </fieldset>

      {/* SECTION 6 */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Seção 6 — Por que TopClass</legend>
        <div><Label>Título</Label><Input value={c.section6_title} onChange={(e) => set("section6_title", e.target.value)} /></div>
        <div><Label>Texto</Label><Textarea rows={4} value={c.section6_body} onChange={(e) => set("section6_body", e.target.value)} /></div>
        <Label>Vantagens</Label>
        {c.section6_bullets.map((f, i) => (
          <div key={i} className="flex gap-2">
            <Input value={f} onChange={(e) => editArray("section6_bullets", i, e.target.value)} />
            <Button type="button" size="icon" variant="ghost" onClick={() => removeArray("section6_bullets", i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addArray("section6_bullets")}><Plus className="h-3 w-3 mr-1" /> Adicionar vantagem</Button>
      </fieldset>

      {/* GUIA DO BAIRRO */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
          Guia do Bairro
        </legend>
        <div><Label>Título da seção</Label><Input value={c.guide_title} onChange={(e) => set("guide_title", e.target.value)} /></div>
        <div><Label>Texto de introdução</Label><Textarea rows={3} value={c.guide_intro} onChange={(e) => set("guide_intro", e.target.value)} /></div>
        <Label className="mt-2">Tópicos</Label>
        {c.guide_entries.map((g, i) => (
          <div key={i} className="border border-border rounded p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Tópico {i + 1}</span>
              <Button type="button" size="icon" variant="ghost" onClick={() => removeGuide(i)}><Trash2 className="h-3 w-3" /></Button>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <div>
                <Label className="text-xs">Emoji</Label>
                <Input value={g.emoji} onChange={(e) => editGuide(i, { emoji: e.target.value })} placeholder="🌳" />
              </div>
              <div>
                <Label className="text-xs">Título</Label>
                <Input value={g.title} onChange={(e) => editGuide(i, { title: e.target.value })} placeholder="Natureza e Bem-estar" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Texto</Label>
              <Textarea rows={4} value={g.body} onChange={(e) => editGuide(i, { body: e.target.value })} />
            </div>
            <ImageUploader
              images={g.image ? [g.image] : []}
              onChange={(imgs) => editGuide(i, { image: imgs[imgs.length - 1] || "" })}
            />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addGuide}><Plus className="h-3 w-3 mr-1" /> Adicionar tópico</Button>
      </fieldset>

        </>
      )}

      {/* FAQ */}

      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">FAQ</legend>
        {c.faq.map((f, i) => (
          <div key={i} className="border border-border rounded p-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">Pergunta {i + 1}</span>
              <Button type="button" size="icon" variant="ghost" onClick={() => removeFaq(i)}><Trash2 className="h-3 w-3" /></Button>
            </div>
            <Input placeholder="Pergunta" value={f.question} onChange={(e) => editFaq(i, { question: e.target.value })} />
            <Textarea rows={2} placeholder="Resposta" value={f.answer} onChange={(e) => editFaq(i, { answer: e.target.value })} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addFaq}><Plus className="h-3 w-3 mr-1" /> Adicionar pergunta</Button>
      </fieldset>

      {/* CLOSING */}
      <fieldset className="space-y-2 border-t border-border pt-4">
        <legend className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Encerramento</legend>
        <div><Label>Título</Label><Input value={c.closing_title} onChange={(e) => set("closing_title", e.target.value)} /></div>
        <div><Label>Texto</Label><Textarea rows={3} value={c.closing_body} onChange={(e) => set("closing_body", e.target.value)} /></div>
        <div><Label>Texto do botão</Label><Input value={c.closing_button} onChange={(e) => set("closing_button", e.target.value)} /></div>
      </fieldset>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={loading}><Save className="h-4 w-4 mr-1" /> Salvar conteúdo</Button>
      </div>
    </div>
  );
};

export default LandingContentEditor;
